import { Sun, MoonStars } from "@phosphor-icons/react";
import useWeather from "@/hooks/useWeather";
import useNow from "@/hooks/useNow";

/**
 * A subtle sunrise → sunset progress line under the morning greeting.
 *
 * Purpose: give the masthead a sense of time flowing through the day
 * without shouting. Renders a hairline arc between two tiny ticks
 * (sunrise / sunset), with a brass dot marking where "now" falls. Before
 * dawn or after dusk the dot pins to the appropriate end and the label
 * switches to "before dawn" / "after sundown" so the rail still tells a
 * coherent story at every hour.
 *
 * Consumes the same shared `useWeather` state as the WeatherWidget, so
 * this surface adds no extra geolocation prompt or network cost. If the
 * user hasn't granted location, the rail renders nothing (the chip up
 * top already handles the "Enable weather" affordance).
 */

// Re-tick every minute so the marker drifts naturally without hammering
// React. Handled via the shared `useNow` hook so every masthead surface
// (dateline, rail, golden-hour blend) shares one interval and stays in
// lockstep.

const fmtTime = (d) =>
  d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });

export default function SunriseSunsetRail() {
  const { status, sunriseIso, sunsetIso } = useWeather({ autoRequest: false });
  const now = useNow();

  // Wait for a resolved weather payload with sunrise + sunset. This is
  // where the rail intentionally stays quiet if permission was denied.
  if (status !== "ok" || !sunriseIso || !sunsetIso) return null;

  const sunrise = new Date(sunriseIso).getTime();
  const sunset = new Date(sunsetIso).getTime();
  if (!Number.isFinite(sunrise) || !Number.isFinite(sunset) || sunset <= sunrise) return null;

  // Where along the rail is "now"? Clamped to [0, 1]; we also expose the
  // raw phase so we can pick the right label + icon.
  const raw = (now - sunrise) / (sunset - sunrise);
  const clamped = Math.max(0, Math.min(1, raw));
  const beforeDawn = raw < 0;
  const afterDusk = raw > 1;
  const phase = beforeDawn ? "night" : afterDusk ? "night" : "day";

  return (
    <div
      className="relative mt-6 max-w-3xl select-none"
      data-testid="sunrise-sunset-rail"
      aria-label={`Day progress from sunrise ${fmtTime(new Date(sunrise))} to sunset ${fmtTime(new Date(sunset))}`}
    >
      {/* Sunrise / sunset labels above the rail */}
      <div className="flex items-center justify-between text-[0.5625rem] font-mono uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
        <span className="inline-flex items-center gap-1">
          <Sun size={9} weight="fill" className="text-amber-500/80" />
          Sunrise {fmtTime(new Date(sunrise))}
        </span>
        <span className="text-brass/70">
          {phase === "night" ? (afterDusk ? "After sundown" : "Before dawn") : "Now"}
        </span>
        <span className="inline-flex items-center gap-1">
          Sunset {fmtTime(new Date(sunset))}
          <MoonStars size={9} weight="fill" className="text-primary/70" />
        </span>
      </div>

      {/* Rail — a hairline tinted along the arc so the middle glows */}
      <div className="relative h-[3px] w-full">
        {/* Base line — muted so the whole rail reads as a hairline */}
        <div className="absolute inset-0 bg-border/60" />
        {/* Daylight gradient — cool at dawn, warm at noon, cool at dusk */}
        <div
          className="absolute inset-y-0 left-0 right-0"
          style={{
            background:
              "linear-gradient(to right, hsl(var(--primary) / 0.35) 0%, hsl(var(--brass) / 0.75) 50%, hsl(var(--primary) / 0.35) 100%)",
            opacity: phase === "day" ? 0.9 : 0.2,
            transition: "opacity 400ms ease",
          }}
        />
        {/* Endpoint ticks — tiny circles at sunrise and sunset */}
        <span className="absolute -top-[3px] -left-[3px] w-[9px] h-[9px] rounded-full bg-background border border-amber-500/70" />
        <span className="absolute -top-[3px] -right-[3px] w-[9px] h-[9px] rounded-full bg-background border border-primary/70" />
        {/* "Now" marker — brass filled dot; sits at the clamped position */}
        <span
          className="absolute -top-[4px] w-[11px] h-[11px] rounded-full bg-brass shadow-[0_0_10px_hsl(var(--brass)/0.75)] transition-[left] duration-500 ease-out"
          style={{ left: `calc(${clamped * 100}% - 5.5px)`, opacity: phase === "day" ? 1 : 0.55 }}
          data-testid="sunrise-sunset-marker"
        />
      </div>
    </div>
  );
}
